/* =============================================================================
   NAJIBS — global behaviour
   No libraries. Native custom elements, defer-loaded, theme-editor aware.
   ============================================================================= */
(function () {
  'use strict';

  const NAJIBS = (window.NAJIBS = window.NAJIBS || {});

  NAJIBS.EVENTS = {
    cartUpdate: 'cart:update',
    variantChange: 'variant:change',
    drawerOpen: 'drawer:open',
    drawerClose: 'drawer:close'
  };

  /* ---------------------------------------------------------------- utilities */

  NAJIBS.prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  NAJIBS.debounce = (fn, wait) => {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  NAJIBS.money = (cents) => {
    const format = NAJIBS.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, dec] = value.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, `${grouped}.${dec}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, grouped)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  };

  const subscribers = {};

  NAJIBS.subscribe = (event, cb) => {
    (subscribers[event] = subscribers[event] || []).push(cb);
    return () => {
      subscribers[event] = subscribers[event].filter((fn) => fn !== cb);
    };
  };

  NAJIBS.publish = (event, data) => {
    (subscribers[event] || []).forEach((cb) => {
      try {
        cb(data);
      } catch (e) {
        console.error(e);
      }
    });
  };

  NAJIBS.announce = (message) => {
    const region = document.getElementById('NajibsAnnouncer');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 60);
  };

  NAJIBS.fetchSection = async (sectionId, params) => {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    const res = await fetch(url.toString());
    if (!res.ok) throw new Error(`Section ${sectionId} failed: ${res.status}`);
    return res.text();
  };

  /* ------------------------------------------------------------- focus trap */

  const FOCUSABLE =
    'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe';

  let trapHandler = null;

  NAJIBS.trapFocus = (container, focusEl) => {
    const elements = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );
    const first = elements[0];
    const last = elements[elements.length - 1];

    NAJIBS.removeTrapFocus();

    trapHandler = (event) => {
      if (event.code !== 'Tab' && event.key !== 'Tab') return;
      if (!container.contains(event.target)) {
        event.preventDefault();
        (first || container).focus();
        return;
      }
      if (event.shiftKey && event.target === first) {
        event.preventDefault();
        (last || container).focus();
      } else if (!event.shiftKey && event.target === last) {
        event.preventDefault();
        (first || container).focus();
      }
    };

    document.addEventListener('keydown', trapHandler);
    const target = focusEl || first || container;
    if (target) {
      if (target === container && !container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }
  };

  NAJIBS.removeTrapFocus = (returnEl) => {
    if (trapHandler) document.removeEventListener('keydown', trapHandler);
    trapHandler = null;
    if (returnEl && typeof returnEl.focus === 'function') returnEl.focus({ preventScroll: true });
  };

  /* -------------------------------------------------------- scroll locking */

  let scrollLocks = 0;
  let savedScrollY = 0;

  NAJIBS.lockScroll = () => {
    if (scrollLocks === 0) {
      savedScrollY = window.scrollY;
      const barWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.top = `-${savedScrollY}px`;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      if (barWidth > 0) document.body.style.paddingInlineEnd = `${barWidth}px`;
      document.body.classList.add('overflow-hidden');
    }
    scrollLocks += 1;
  };

  NAJIBS.unlockScroll = () => {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.paddingInlineEnd = '';
      document.body.classList.remove('overflow-hidden');
      window.scrollTo(0, savedScrollY);
    }
  };

  /* =========================================================== <najibs-drawer> */

  class NajibsDrawer extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.overlay = this.querySelector('[data-drawer-overlay]');
      this.panel = this.querySelector('[data-drawer-panel]');
      this.opener = null;

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target === this.overlay) {
          e.preventDefault();
          this.close();
        }
      });

      this.escHandler = (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) {
          e.stopPropagation();
          this.close();
        }
      };
    }

    open(opener) {
      if (this.classList.contains('is-open')) return;
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      NAJIBS.lockScroll();
      document.addEventListener('keydown', this.escHandler);
      requestAnimationFrame(() => {
        this.classList.add('is-active');
        const focusTarget = this.querySelector('[data-drawer-focus]') || this.panel;
        NAJIBS.trapFocus(this.panel || this, focusTarget);
      });
      NAJIBS.publish(NAJIBS.EVENTS.drawerOpen, { drawer: this });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-active');
      this.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', this.escHandler);
      NAJIBS.removeTrapFocus(this.opener);
      NAJIBS.unlockScroll();
      const done = () => {
        this.classList.remove('is-open');
        this.removeEventListener('transitionend', done);
      };
      if (NAJIBS.prefersReducedMotion()) done();
      else {
        this.addEventListener('transitionend', done);
        window.setTimeout(done, 700);
      }
      NAJIBS.publish(NAJIBS.EVENTS.drawerClose, { drawer: this });
    }

    toggle(opener) {
      this.classList.contains('is-open') ? this.close() : this.open(opener);
    }
  }

  customElements.define('najibs-drawer', NajibsDrawer);

  /* Any element with [data-drawer-open="ID"] controls a drawer. */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.getAttribute('data-drawer-open'));
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(trigger);
  });

  /* ====================================================== <najibs-cart-drawer> */

  /* Only ask the Section Rendering API for sections that are actually on the page.
     With settings.cart_type == 'page' the drawer is never rendered, so rendering it
     server-side on every mutation is work nobody reads. */
  const cartSections = () =>
    NAJIBS.cartType === 'drawer'
      ? 'cart-drawer,cart-icon-bubble,cart-live-region'
      : 'cart-icon-bubble,cart-live-region';

  class NajibsCartDrawer extends NajibsDrawer {
    connectedCallback() {
      super.connectedCallback();
      NAJIBS.subscribe(NAJIBS.EVENTS.cartUpdate, (data) => this.render(data));
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cart-quantity]');
        if (input) this.changeLine(input.dataset.line, input.value, input);
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) {
          e.preventDefault();
          this.changeLine(remove.dataset.line, 0, remove);
        }
      });
    }

    async changeLine(line, quantity, source) {
      this.setBusy(true);
      try {
        const res = await fetch(`${NAJIBS.routes.cart_change}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: Number(line),
            quantity: Number(quantity),
            sections: cartSections(),
            sections_url: window.location.pathname
          })
        });
        const data = await res.json();
        if (data.status) throw new Error(data.description || data.message);
        NAJIBS.publish(NAJIBS.EVENTS.cartUpdate, data);
        if (Number(quantity) === 0) NAJIBS.announce(NAJIBS.strings.itemRemoved);
      } catch (err) {
        NAJIBS.announce(NAJIBS.strings.cartError);
        console.error(err);
      } finally {
        this.setBusy(false);
        if (source && source.dataset.line) {
          const again = this.querySelector(`[data-cart-quantity][data-line="${source.dataset.line}"]`);
          if (again) again.focus({ preventScroll: true });
        }
      }
    }

    setBusy(state) {
      this.toggleAttribute('data-busy', state);
      this.querySelectorAll('[data-cart-quantity], [data-cart-remove]').forEach((el) => {
        el.disabled = state;
      });
    }

    render(data) {
      const sections = data && data.sections;
      if (!sections) return;
      Object.entries({
        'cart-drawer': '[data-cart-drawer-contents]',
        'cart-icon-bubble': '[data-cart-bubble]',
        'cart-live-region': '[data-cart-live-region]'
      }).forEach(([sectionKey, selector]) => {
        const html = sections[sectionKey];
        if (!html) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector(selector);
        document.querySelectorAll(selector).forEach((target) => {
          if (source) target.innerHTML = source.innerHTML;
        });
      });
      this.dataset.cartCount = data.item_count != null ? data.item_count : '';
    }
  }

  customElements.define('najibs-cart-drawer', NajibsCartDrawer);

  /* ------------------------------------------------- add to cart (any form) */

  NAJIBS.addToCart = async (form, button) => {
    const formData = new FormData(form);
    formData.append('sections', cartSections());
    formData.append('sections_url', window.location.pathname);

    if (button) button.classList.add('btn--loading');
    const errorEl = form.querySelector('[data-cart-error]');
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    try {
      const res = await fetch(`${NAJIBS.routes.cart_add}.js`, {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData
      });
      const data = await res.json();

      if (data.status) {
        if (errorEl) {
          errorEl.textContent = data.description || data.message;
          errorEl.hidden = false;
        }
        NAJIBS.announce(data.description || data.message);
        return { ok: false, data };
      }

      const cartRes = await fetch(`${NAJIBS.routes.cart}.js?sections=${cartSections()}`);
      const cart = await cartRes.json();
      cart.sections = data.sections || cart.sections;
      NAJIBS.publish(NAJIBS.EVENTS.cartUpdate, cart);
      NAJIBS.announce(NAJIBS.strings.added);

      const drawer = document.getElementById('CartDrawer');
      if (NAJIBS.cartType === 'drawer' && drawer && typeof drawer.open === 'function') {
        drawer.open(button);
      } else if (NAJIBS.cartType !== 'drawer') {
        window.location.href = NAJIBS.routes.cart;
      }
      return { ok: true, data };
    } catch (err) {
      console.error(err);
      NAJIBS.announce(NAJIBS.strings.cartError);
      return { ok: false };
    } finally {
      if (button) button.classList.remove('btn--loading');
    }
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-ajax-cart]');
    if (!form) return;
    event.preventDefault();
    NAJIBS.addToCart(form, form.querySelector('[data-add-button]'));
  });

  /* Keep the bubble in sync even when another script updates the cart. */
  NAJIBS.subscribe(NAJIBS.EVENTS.cartUpdate, (data) => {
    if (!data || !data.sections) return;
    const parsed = new DOMParser().parseFromString(data.sections['cart-icon-bubble'] || '', 'text/html');
    const source = parsed.querySelector('[data-cart-bubble]');
    if (!source) return;
    document.querySelectorAll('[data-cart-bubble]').forEach((el) => {
      el.innerHTML = source.innerHTML;
    });
  });

  /* ========================================================== <najibs-header> */

  class NajibsHeader extends HTMLElement {
    connectedCallback() {
      this.sticky = this.dataset.sticky === 'true';
      this.hideOnScroll = this.dataset.hideOnScroll === 'true';
      this.lastY = window.scrollY;
      this.threshold = 8;

      this.setHeight();
      this.resizeObserver = new ResizeObserver(() => this.setHeight());
      this.resizeObserver.observe(this);

      this.onScroll = () => this.handleScroll();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.handleScroll();

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeMenus();
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    setHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    }

    handleScroll() {
      const y = window.scrollY;
      const scrolled = y > 24;
      this.classList.toggle('header--scrolled', scrolled);
      document.documentElement.classList.toggle('header-scrolled', scrolled);

      if (this.hideOnScroll && this.sticky) {
        const openMenu = this.querySelector('[open]');
        if (!openMenu && Math.abs(y - this.lastY) > this.threshold) {
          this.classList.toggle('header--hidden', y > this.lastY && y > 200);
        }
      }
      this.lastY = y;
    }

    closeMenus() {
      this.querySelectorAll('details[open]').forEach((d) => d.removeAttribute('open'));
    }
  }

  customElements.define('najibs-header', NajibsHeader);

  /* Close any open <details> menu when focus or a click leaves it. */
  document.addEventListener('click', (event) => {
    document.querySelectorAll('details[data-menu][open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });

  document.addEventListener('focusin', (event) => {
    document.querySelectorAll('details[data-menu][open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });

  /* ========================================================== <najibs-slider> */

  class NajibsSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');

      this.update = NAJIBS.debounce(() => this.updateButtons(), 80);
      this.track.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update, { passive: true });

      if (this.prev) this.prev.addEventListener('click', () => this.scrollBy(-1));
      if (this.next) this.next.addEventListener('click', () => this.scrollBy(1));
      this.updateButtons();
    }

    get step() {
      const first = this.track.firstElementChild;
      if (!first) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
      return first.getBoundingClientRect().width + gap;
    }

    scrollBy(direction) {
      const rtl = getComputedStyle(this).direction === 'rtl';
      this.track.scrollBy({
        left: this.step * direction * (rtl ? -1 : 1),
        behavior: NAJIBS.prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    updateButtons() {
      if (!this.prev && !this.next) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const pos = Math.abs(scrollLeft);
      const atStart = pos <= 2;
      const atEnd = pos + clientWidth >= scrollWidth - 2;
      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;
      this.toggleAttribute('data-no-overflow', scrollWidth <= clientWidth + 2);
    }
  }

  customElements.define('najibs-slider', NajibsSlider);

  /* ======================================================== <najibs-quantity> */

  class NajibsQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="number"]');
      if (!this.input) return;
      this.querySelectorAll('button[data-qty-step]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const dir = Number(btn.dataset.qtyStep);
          const min = Number(this.input.min || 1);
          const max = this.input.max ? Number(this.input.max) : Infinity;
          const next = Math.min(max, Math.max(min, Number(this.input.value || min) + dir));
          if (next === Number(this.input.value)) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }

  customElements.define('najibs-quantity', NajibsQuantity);

  /* ======================================================= <najibs-quick-add> */

  class NajibsQuickAdd extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-quick-add-button]');
      if (!this.button) return;
      this.button.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();

        /* Single-variant products add straight to the cart. Anything with real
           choices opens the product's own picker — we never guess a variant. */
        if (this.dataset.variantId) {
          const form = this.querySelector('form');
          if (form) await NAJIBS.addToCart(form, this.button);
          return;
        }

        const modal = document.getElementById('QuickAddModal');
        if (!modal || !this.dataset.productUrl) {
          window.location.href = this.dataset.productUrl || '#';
          return;
        }
        this.button.classList.add('btn--loading');
        try {
          const url = new URL(this.dataset.productUrl, window.location.origin);
          url.searchParams.set('section_id', 'quick-add');
          const res = await fetch(url.toString());
          const html = await res.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const content = parsed.querySelector('[data-quick-add-content]');
          const target = modal.querySelector('[data-quick-add-target]');
          if (content && target) {
            target.innerHTML = content.innerHTML;
            modal.open(this.button);
            NAJIBS.publish('quickadd:loaded', { container: target });
          }
        } catch (err) {
          console.error(err);
          window.location.href = this.dataset.productUrl;
        } finally {
          this.button.classList.remove('btn--loading');
        }
      });
    }
  }

  customElements.define('najibs-quick-add', NajibsQuickAdd);

  /* ====================================================== <najibs-accordion> */

  class NajibsAccordion extends HTMLElement {
    connectedCallback() {
      if (this.dataset.exclusive !== 'true') return;
      this.addEventListener('toggle', (event) => {
        const item = event.target;
        if (!item.open) return;
        this.querySelectorAll('details[open]').forEach((other) => {
          if (other !== item) other.removeAttribute('open');
        });
      }, true);
    }
  }

  customElements.define('najibs-accordion', NajibsAccordion);

  /* =================================================== <najibs-localization> */

  class NajibsLocalization extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-localization-value]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const input = this.querySelector('input[name="country_code"], input[name="locale_code"]');
          if (!input) return;
          input.value = button.dataset.localizationValue;
          const form = this.querySelector('form');
          if (form) form.submit();
        });
      });
    }
  }

  customElements.define('najibs-localization', NajibsLocalization);

  /* ================================================ <najibs-predictive-search> */

  class NajibsPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results) return;

      this.options = [];
      this.cursor = -1;

      this.onInput = NAJIBS.debounce(() => this.search(), 260);
      this.input.addEventListener('input', this.onInput);
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-autocomplete', 'list');

      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    /* The listbox and its options are rendered by snippets/predictive-search-results.liquid
       and arrive with their own ids; anything missing one gets a generated id so
       aria-activedescendant always has something real to point at. */
    collectOptions() {
      this.options = Array.from(this.results.querySelectorAll('[role="option"]'));
      this.options.forEach((option, i) => {
        if (!option.id) option.id = `PredictiveOption-${i}`;
      });

      const listbox = this.results.querySelector('[role="listbox"]');
      if (listbox) {
        if (!listbox.id) listbox.id = 'PredictiveSearchListbox';
        this.input.setAttribute('aria-controls', listbox.id);
      } else {
        this.input.removeAttribute('aria-controls');
      }
    }

    clearHighlight() {
      this.options.forEach((option) => {
        option.classList.remove('is-highlighted');
        option.setAttribute('aria-selected', 'false');
      });
      this.cursor = -1;
      this.input.removeAttribute('aria-activedescendant');
    }

    move(step) {
      const total = this.options.length;
      if (!total) return;
      const from = this.cursor < 0 ? (step > 0 ? -1 : 0) : this.cursor;
      const next = (from + step + total) % total;

      this.options.forEach((option, i) => {
        const on = i === next;
        option.classList.toggle('is-highlighted', on);
        option.setAttribute('aria-selected', String(on));
      });

      this.cursor = next;
      const active = this.options[next];
      this.input.setAttribute('aria-activedescendant', active.id);
      if (typeof active.scrollIntoView === 'function') active.scrollIntoView({ block: 'nearest' });
    }

    onKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          if (!this.options.length) return;
          event.preventDefault();
          this.move(1);
          break;
        case 'ArrowUp':
          if (!this.options.length) return;
          event.preventDefault();
          this.move(-1);
          break;
        case 'Enter': {
          const active = this.options[this.cursor];
          if (!active) return; /* nothing picked — the form submits to the results page */
          event.preventDefault();
          active.click();
          break;
        }
        case 'Escape':
          /* First Escape drops the cursor, a second one closes the search. */
          if (this.cursor > -1) {
            event.preventDefault();
            event.stopPropagation();
            this.clearHighlight();
          } else {
            this.reset();
          }
          break;
        default:
          break;
      }
    }

    reset() {
      this.clearHighlight();
      this.options = [];
      this.results.innerHTML = '';
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-controls');
    }

    async search() {
      const term = this.input.value.trim();
      if (term.length < 2) return this.reset();
      this.setAttribute('data-loading', '');
      try {
        const url = new URL(NAJIBS.routes.predictive_search, window.location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('resources[type]', 'product,collection,page,article');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('section_id', 'predictive-search');
        const res = await fetch(url.toString());
        const html = await res.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const content = parsed.querySelector('[data-predictive-results]');
        this.clearHighlight();
        this.results.innerHTML = content ? content.innerHTML : '';
        this.results.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
        this.collectOptions();
      } catch (err) {
        console.error(err);
        this.reset();
      } finally {
        this.removeAttribute('data-loading');
      }
    }
  }

  customElements.define('najibs-predictive-search', NajibsPredictiveSearch);

  /* ================================================ <najibs-recently-viewed> */

  const RECENT_KEY = 'najibs:recently-viewed';

  NAJIBS.rememberProduct = (handle) => {
    if (!handle) return;
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter((h) => h !== handle);
      list.unshift(handle);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 12)));
    } catch (e) {
      /* private mode — recently viewed simply does not persist */
    }
  };

  class NajibsRecentlyViewed extends HTMLElement {
    async connectedCallback() {
      let handles = [];
      try {
        handles = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      } catch (e) {
        return;
      }
      const exclude = this.dataset.exclude;
      handles = handles.filter((h) => h && h !== exclude).slice(0, Number(this.dataset.limit || 6));
      if (!handles.length) return;

      const query = handles.map((h) => `handle:${h}`).join(' OR ');
      const url = new URL(NAJIBS.routes.search, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('section_id', this.dataset.sectionId);
      try {
        const res = await fetch(url.toString());
        const html = await res.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const content = parsed.querySelector('[data-recently-viewed-content]');
        if (content && content.querySelector('.card')) {
          this.innerHTML = content.innerHTML;
          this.hidden = false;
          initReveals(this);
        }
      } catch (err) {
        console.error(err);
      }
    }
  }

  customElements.define('najibs-recently-viewed', NajibsRecentlyViewed);

  /* ======================================================== <najibs-hotspots> */

  class NajibsHotspots extends HTMLElement {
    connectedCallback() {
      this.dots = Array.from(this.querySelectorAll('[data-hotspot]'));
      this.cards = Array.from(this.querySelectorAll('[data-hotspot-card]'));
      if (!this.dots.length) return;

      this.dots.forEach((dot) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          this.activate(dot.dataset.hotspot);
        });
        dot.addEventListener('mouseenter', () => this.activate(dot.dataset.hotspot));
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.activate(null);
      });
    }

    activate(id) {
      this.dots.forEach((dot) => {
        const on = dot.dataset.hotspot === id;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-expanded', String(on));
      });
      this.cards.forEach((card) => {
        card.classList.toggle('is-active', card.dataset.hotspotCard === id);
      });
    }
  }

  customElements.define('najibs-hotspots', NajibsHotspots);

  /* ======================================================= <najibs-load-more> */

  class NajibsLoadMore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.target = document.querySelector(this.dataset.target);
      if (!this.button || !this.target) return;
      this.button.addEventListener('click', () => this.load());
    }

    async load() {
      const nextUrl = this.dataset.nextUrl;
      if (!nextUrl) return;
      this.button.classList.add('btn--loading');
      try {
        const url = new URL(nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const res = await fetch(url.toString());
        const html = await res.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const newItems = parsed.querySelector(this.dataset.target);
        const newLoader = parsed.querySelector('najibs-load-more');
        if (newItems) {
          const frag = document.createDocumentFragment();
          Array.from(newItems.children).forEach((child) => frag.appendChild(child));
          this.target.appendChild(frag);
          initReveals(this.target);
        }
        if (newLoader && newLoader.dataset.nextUrl) {
          this.dataset.nextUrl = newLoader.dataset.nextUrl;
        } else {
          this.remove();
        }
        NAJIBS.announce(this.dataset.announce || '');
      } catch (err) {
        console.error(err);
      } finally {
        if (this.button) this.button.classList.remove('btn--loading');
      }
    }
  }

  customElements.define('najibs-load-more', NajibsLoadMore);

  /* ====================================================== scroll reveals */

  let revealObserver = null;
  let revealsFired = 0;

  function initReveals(root) {
    const scope = root || document;
    const targets = scope.querySelectorAll('[data-animate]:not(.is-visible)');
    if (!targets.length) return;

    if (NAJIBS.prefersReducedMotion()) {
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            revealsFired += 1;
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
      );
    }

    targets.forEach((el) => {
      const delay = Number(el.dataset.animateDelay || 0);
      if (delay) el.style.setProperty('--animate-delay', `${delay * 80}ms`);
      revealObserver.observe(el);
    });
  }

  function revealEverything() {
    document.querySelectorAll('[data-animate]:not(.is-visible)').forEach((el) => el.classList.add('is-visible'));
  }

  /* Second safety net, for a script that loaded and then misbehaved.
     The first one is not in this file and cannot be: layout/theme.liquid hides reveal
     targets by putting .reveals-on on <html>, and takes it off again at DOMContentLoaded
     unless NAJIBS.initReveals exists — so a blocked or throwing global.js can never leave
     the page blank. What that cannot catch is this file running fine and the observer
     then never firing. So: if, a moment after load, nothing has been revealed at all,
     treat the observer as unavailable and show everything. A healthy page always fires
     for whatever is already on screen, so this never interferes with scroll reveals. */
  function armRevealFallback() {
    window.setTimeout(() => {
      if (revealsFired === 0) revealEverything();
    }, 1200);
  }

  if (!('IntersectionObserver' in window)) {
    revealEverything();
    document.addEventListener('DOMContentLoaded', revealEverything);
  } else if (document.readyState === 'complete') {
    armRevealFallback();
  } else {
    window.addEventListener('load', armRevealFallback, { once: true });
  }

  NAJIBS.initReveals = initReveals;

  /* ====================================================== boot + editor hooks */

  function boot(root) {
    initReveals(root);
    /* Float labels for inputs that arrive pre-filled by the browser. */
    (root || document).querySelectorAll('.field__input, .field__textarea').forEach((input) => {
      if (input.value) input.closest('.field')?.classList.add('field--filled');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', () => {
    document.body.classList.remove('overflow-hidden');
  });
  document.addEventListener('shopify:block:select', (event) => {
    const drawer = event.target.closest('najibs-drawer, najibs-cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  });
})();
